/**
 * PagoDetalleModal (rc50) — modal de lectura pura con info extendida del pago.
 *
 * Secciones colapsables:
 *   1. Resumen        — folio, fecha, monto, cliente, factura, badge estado
 *   2. Origen         — toku transfer/card/cash, manual, saldo a favor
 *   3. Detalle Toku   — sólo si payment_provider='toku': timeline status_history
 *                       + payment_instrument completo + payment_intents + wallet_movements
 *   4. Conciliación   — qué se hizo con el dinero (factura aplicada, surplus, etc)
 *   5. Auditoría      — event_id webhook, processing_status, retry, anulación
 *
 * No acciones (sólo "Imprimir recibo" + "Ver en Toku" link).
 * Toda data viene del endpoint /api/v1/pagos/{id}/detalle — NO llama Toku API.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Receipt, FileText, User as UserIcon, Building2,
  CreditCard, ArrowRightLeft, Banknote, Sparkles, ShieldCheck,
  AlertTriangle, ExternalLink, Printer, Copy, Check,
  ChevronDown, ChevronRight, Clock, Activity, Info,
} from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/icons/Icon";
import { formatMXN, formatDate, formatDateTime } from "@/lib/utils";
import { abrirReciboPago } from "@/features/config/api";

import { usePagoDetalle } from "./api";
import type {
  PagoDetalle, EstadoConciliacion, OrigenPago,
  TokuTransactionDetail, TokuStatusHistoryEntry,
} from "./schemas";

type Props = {
  pagoId: string | null;
  onClose: () => void;
};

export function PagoDetalleModal({ pagoId, onClose }: Props) {
  const { data, isLoading, error } = usePagoDetalle(pagoId);

  return (
    <Dialog open={pagoId !== null} onOpenChange={(v) => { if (!v) onClose(); }} size="2xl">
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet size={16} className="text-primary" />
            Detalle del pago
            {data && <span className="font-mono text-xs text-muted-foreground">{data.folio}</span>}
          </DialogTitle>
        </DialogHeader>

        {isLoading && <DetalleSkeleton />}
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertTriangle size={14} className="inline mr-1" />
            No se pudo cargar el detalle: {(error as Error).message}
          </div>
        )}
        {data && <DetalleContent data={data} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

// ───── Skeleton de loading ───────────────────────────────────────────────────
function DetalleSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-24 w-full rounded-lg bg-muted/60" />
      <div className="h-16 w-full rounded-lg bg-muted/40" />
      <div className="h-32 w-full rounded-lg bg-muted/40" />
    </div>
  );
}

// ───── Contenido ─────────────────────────────────────────────────────────────
function DetalleContent({ data, onClose }: { data: PagoDetalle; onClose: () => void }) {
  return (
    <div className="space-y-3">
      <ResumenSection data={data} onClose={onClose} />
      <OrigenSection data={data} />
      {data.toku && <TokuSection toku={data.toku} />}
      <ConciliacionSection data={data} />
      <AuditoriaSection data={data} />
    </div>
  );
}

// ───── Sección 1: Resumen ────────────────────────────────────────────────────
function ResumenSection({ data, onClose }: { data: PagoDetalle; onClose: () => void }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-[10px] uppercase text-muted-foreground">Monto</div>
          <div className="font-mono text-2xl font-semibold">{formatMXN(Number(data.monto))}</div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <EstadoBadge estado={data.estado_conciliacion} />
            <OrigenBadge origen={data.origen} />
            {data.anulado && (
              <Badge variant="destructive" className="text-[10px]">ANULADO</Badge>
            )}
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="text-[10px] uppercase text-muted-foreground">Fecha</div>
          <div className="text-sm">{formatDate(data.fecha)}</div>
          <div className="text-[10px] text-muted-foreground">
            Registrado {formatDateTime(data.created_at)}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <InfoRow
          icon={<Building2 size={12} />}
          label="Cliente"
          value={
            <Link
              to={`/clientes/${data.cliente.id}`}
              onClick={onClose}
              className="text-primary hover:underline"
            >
              {data.cliente.codigo} · {data.cliente.razon_social}
            </Link>
          }
        />
        <InfoRow
          icon={<Receipt size={12} />}
          label="Factura"
          value={
            data.factura ? (
              <Link
                to={`/facturas/${data.factura.id}`}
                onClick={onClose}
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {data.factura.folio} <ExternalLink size={10} />
              </Link>
            ) : (
              <span className="text-muted-foreground italic">— sin factura (saldo a favor)</span>
            )
          }
        />
        <InfoRow icon={<FileText size={12} />} label="Método" value={<span className="capitalize">{data.metodo}</span>} />
        <InfoRow icon={<UserIcon size={12} />} label="Registrado por"
          value={data.cajero ? (data.cajero.nombre_completo || data.cajero.email) : "—"} />
        {data.referencia && (
          <InfoRow icon={<Info size={12} />} label="Referencia"
            value={<span className="font-mono text-[11px]">{data.referencia}</span>} />
        )}
        {data.notas && (
          <InfoRow icon={<FileText size={12} />} label="Notas" value={<span>{data.notas}</span>} />
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {!data.anulado && (
          <Button
            variant="outline" size="sm"
            onClick={() => {
              abrirReciboPago(data.id).catch((err) =>
                toast.error("Error al abrir recibo", { description: (err as Error).message })
              );
            }}
          >
            <Printer size={12} className="mr-1.5" /> Imprimir recibo
          </Button>
        )}
      </div>
    </div>
  );
}

// ───── Sección 2: Origen ─────────────────────────────────────────────────────
function OrigenSection({ data }: { data: PagoDetalle }) {
  const isToku = data.origen.startsWith("toku_");
  const isManual = data.origen === "manual";
  const isSaldo = data.origen === "saldo_a_favor";

  return (
    <CollapsibleSection
      title="Origen del pago"
      icon={<Sparkles size={14} />}
      defaultOpen
    >
      {isToku && (
        <div className="text-xs text-muted-foreground">
          Procesado automáticamente vía <span className="font-medium text-foreground">Toku</span>.
          {data.toku?.toku_product && (
            <> Producto: <span className="font-mono">{data.toku.toku_product}</span>.</>
          )}
          {data.toku?.gateway && (
            <> Gateway: <span className="font-mono">{data.toku.gateway}</span>.</>
          )}
        </div>
      )}
      {isManual && (
        <div className="text-xs text-muted-foreground">
          Registrado manualmente por
          {data.cajero ? (
            <span className="font-medium text-foreground"> {data.cajero.nombre_completo || data.cajero.email}</span>
          ) : (
            " un usuario del sistema"
          )}.
        </div>
      )}
      {isSaldo && (
        <div className="text-xs text-muted-foreground">
          Aplicado automáticamente desde saldo a favor del cliente.
        </div>
      )}
    </CollapsibleSection>
  );
}

// ───── Sección 3: Detalle Toku ───────────────────────────────────────────────
function TokuSection({ toku }: { toku: TokuTransactionDetail }) {
  return (
    <CollapsibleSection
      title="Detalle Toku"
      icon={<Activity size={14} />}
      defaultOpen
      badge={toku.status === "SUCCESS" ? "success" : toku.status === "FAILED" ? "destructive" : "muted"}
      badgeText={toku.status ?? undefined}
    >
      <div className="space-y-3">
        {/* IDs + meta */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 text-xs">
          {toku.transaction_id && (
            <CopyableRow label="Transaction ID" value={toku.transaction_id} mono />
          )}
          {toku.amount && (
            <InfoRow label="Monto Toku"
              value={<span className="font-mono">{formatMXN(Number(toku.amount))} {toku.currency}</span>} />
          )}
          {toku.transaction_date && (
            <InfoRow label="Fecha pago Toku" value={formatDateTime(toku.transaction_date)} />
          )}
          {toku.response_code && (
            <InfoRow label="Response code" value={<span className="font-mono">{toku.response_code}</span>} />
          )}
          {toku.remediation_detail && (
            <div className="col-span-full rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={12} className="inline mr-1" />
              <span className="font-medium">Remediación: </span>{toku.remediation_detail}
            </div>
          )}
        </div>

        {/* Status history timeline */}
        {toku.status_history.length > 0 && (
          <div>
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">Timeline</div>
            <div className="space-y-1">
              {toku.status_history.map((h, i) => (
                <StatusHistoryRow key={i} entry={h} />
              ))}
            </div>
          </div>
        )}

        {/* Payment instrument por tipo */}
        {toku.instrument_type === "transfer" && toku.instrument_transfer && (
          <TransferDetail t={toku.instrument_transfer} />
        )}
        {toku.instrument_type === "card" && toku.instrument_card && (
          <CardDetail c={toku.instrument_card} />
        )}
        {toku.instrument_type === "cash" && toku.instrument_cash && (
          <CashDetail c={toku.instrument_cash} />
        )}
        {toku.instrument_other && Object.keys(toku.instrument_other).length > 0 && (
          <div className="rounded-md border bg-muted/30 p-2 text-[11px]">
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">
              Instrumento ({toku.instrument_type || "desconocido"})
            </div>
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono">
              {JSON.stringify(toku.instrument_other, null, 2)}
            </pre>
          </div>
        )}

        {/* Pagador info pill — si payer_name != customer_name */}
        {toku.instrument_transfer?.payer_name &&
          toku.customer_name &&
          toku.instrument_transfer.payer_name.toLowerCase().trim() !==
            toku.customer_name.toLowerCase().trim() && (
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-2 text-[11px] text-blue-700 dark:text-blue-300">
            <Info size={11} className="inline mr-1" />
            Pagador distinto al titular del contrato
            (<span className="font-medium">{toku.instrument_transfer.payer_name}</span> ≠ {toku.customer_name})
          </div>
        )}

        {/* Customer Toku */}
        {(toku.customer_id || toku.customer_external_id) && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 text-xs">
            {toku.customer_id && <CopyableRow label="Customer Toku" value={toku.customer_id} mono />}
            {toku.customer_external_id && (
              <InfoRow label="External ID" value={<span className="font-mono">{toku.customer_external_id}</span>} />
            )}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

function TransferDetail({ t }: { t: NonNullable<TokuTransactionDetail["instrument_transfer"]> }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase text-muted-foreground">
        <ArrowRightLeft size={11} /> Transferencia SPEI
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
        {t.payer_name && <InfoRow label="Pagador" value={t.payer_name} />}
        {t.payer_clabe && <CopyableRow label="CLABE pagador" value={t.payer_clabe} mono />}
        {t.tracking_id && <CopyableRow label="Tracking SPEI" value={t.tracking_id} mono />}
        {t.beneficiary_clabe && <CopyableRow label="CLABE destino" value={t.beneficiary_clabe} mono />}
        {t.transaction_date && (
          <InfoRow label="Fecha banco" value={formatDateTime(t.transaction_date)} />
        )}
      </div>
    </div>
  );
}

function CardDetail({ c }: { c: NonNullable<TokuTransactionDetail["instrument_card"]> }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase text-muted-foreground">
        <CreditCard size={11} /> Tarjeta {c.card_type}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
        {c.card_brand && <InfoRow label="Marca" value={c.card_brand} />}
        {c.last_digits && (
          <InfoRow label="Final" value={<span className="font-mono">**** {c.last_digits}</span>} />
        )}
        {c.card_holder && <InfoRow label="Tarjetahabiente" value={c.card_holder} />}
        {c.issuer && <InfoRow label="Banco" value={c.issuer} />}
        {c.installments_number != null && c.installments_number > 1 && (
          <InfoRow label="Meses" value={String(c.installments_number)} />
        )}
        {c.three_d_secure != null && (
          <InfoRow label="3DS"
            value={c.three_d_secure ? <span className="text-emerald-500"><ShieldCheck size={11} className="inline mr-1" />Sí</span> : "No"} />
        )}
      </div>
    </div>
  );
}

function CashDetail({ c }: { c: NonNullable<TokuTransactionDetail["instrument_cash"]> }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase text-muted-foreground">
        <Banknote size={11} /> Efectivo
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
        {c.store && <InfoRow label="Tienda" value={c.store} />}
        {c.voucher && <CopyableRow label="Voucher" value={c.voucher} mono />}
        {c.barcode && <InfoRow label="Código de barras" value={<span className="font-mono">{c.barcode}</span>} />}
      </div>
    </div>
  );
}

// ───── Sección 4: Conciliación ───────────────────────────────────────────────
function ConciliacionSection({ data }: { data: PagoDetalle }) {
  const factura = data.factura;
  const tokuSurplus = data.toku?.surplus_total ? Number(data.toku.surplus_total) : 0;
  const intents = data.toku?.payment_intents ?? [];

  return (
    <CollapsibleSection
      title="Conciliación"
      icon={<ShieldCheck size={14} />}
      defaultOpen
    >
      <div className="space-y-2 text-xs">
        {factura && (
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Aplicado a factura</div>
            <div className="flex items-center justify-between gap-2">
              <span>
                <Link to={`/facturas/${factura.id}`} className="text-primary hover:underline font-mono">
                  {factura.folio}
                </Link>
                <span className="ml-2 text-muted-foreground">total {formatMXN(Number(factura.monto_total))}</span>
              </span>
              {/* rc58: usa ESTATUS_VARIANT (helpers facturas) — pagada=success verde,
                  vencida=destructive, parcial=warning, etc. Antes pintaba todo
                  en azul primary y se perdía la semántica. */}
              <Badge
                variant={
                  factura.estatus === "pagada"
                    ? "success"
                    : factura.estatus === "vencida"
                    ? "destructive"
                    : factura.estatus === "parcial"
                    ? "warning"
                    : "secondary"
                }
              >
                {factura.estatus}
              </Badge>
            </div>
            <div className="mt-1 text-muted-foreground">
              Saldo restante: <span className="font-mono">{formatMXN(Number(factura.saldo))}</span>
            </div>
          </div>
        )}

        {!factura && !data.anulado && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-amber-700 dark:text-amber-300">
            <AlertTriangle size={12} className="inline mr-1" />
            Sin factura aplicada — el monto está como saldo a favor del cliente.
            Se aplicará automáticamente a la próxima factura emitida.
          </div>
        )}

        {tokuSurplus > 0 && (
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-blue-700 dark:text-blue-300">
            <Info size={12} className="inline mr-1" />
            Toku reportó <span className="font-mono">{formatMXN(tokuSurplus)}</span> como excedente (SURPLUS) — registrado como saldo a favor.
          </div>
        )}

        {intents.length > 0 && (
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Payment intents Toku ({intents.length})</div>
            <div className="space-y-1">
              {intents.map((pi, i) => (
                <div key={pi.id ?? i} className="rounded border bg-card p-2 font-mono text-[10px]">
                  {pi.invoice_external_id && <div>invoice_ext: {pi.invoice_external_id}</div>}
                  {pi.amount && <div>monto: {formatMXN(Number(pi.amount))} {pi.currency}</div>}
                  {pi.status && <div>status: {pi.status}</div>}
                  {pi.is_partial && <div className="text-amber-600">pago parcial</div>}
                  {pi.is_last_payment && <div className="text-emerald-600">último pago</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

// ───── Sección 5: Auditoría ──────────────────────────────────────────────────
function AuditoriaSection({ data }: { data: PagoDetalle }) {
  return (
    <CollapsibleSection title="Auditoría" icon={<Clock size={14} />} defaultOpen={false}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
        <InfoRow label="Pago ID" value={<span className="font-mono text-[10px]">{data.id}</span>} />
        <InfoRow label="Folio" value={<span className="font-mono">{data.folio}</span>} />
        <InfoRow label="Registrado" value={formatDateTime(data.created_at)} />
        {data.payment_provider && (
          <InfoRow label="Provider" value={<span className="font-mono">{data.payment_provider}</span>} />
        )}
        {data.payment_provider_payment_id && (
          <CopyableRow label="Provider payment id" value={data.payment_provider_payment_id} mono />
        )}
        {data.payment_provider_synced_at && (
          <InfoRow label="Sincronizado" value={formatDateTime(data.payment_provider_synced_at)} />
        )}
        {data.toku?.event_id && (
          <InfoRow label="Webhook event" value={<span className="font-mono text-[10px]">{data.toku.event_id}</span>} />
        )}
        {data.toku?.processing_status && (
          <InfoRow label="Webhook status" value={data.toku.processing_status} />
        )}
        {data.toku?.retry_count != null && data.toku.retry_count > 0 && (
          <InfoRow label="Webhook retries" value={String(data.toku.retry_count)} />
        )}
        {data.anulado && (
          <>
            <InfoRow label="Anulado" value={data.anulado_at ? formatDateTime(data.anulado_at) : "—"} />
            <InfoRow label="Motivo" value={data.motivo_anulacion ?? "—"} />
            <InfoRow label="Anulado por" value={data.anulado_por?.nombre_completo || data.anulado_por?.email || "—"} />
          </>
        )}
      </div>
    </CollapsibleSection>
  );
}

// ─── Helpers UI ──────────────────────────────────────────────────────────────
function CollapsibleSection({
  title, icon, children, defaultOpen, badge, badgeText,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: "success" | "destructive" | "muted";
  badgeText?: string;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const badgeClass =
    badge === "success" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" :
    badge === "destructive" ? "bg-destructive/15 text-destructive" :
    "bg-muted text-muted-foreground";
  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-muted/30"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          {icon}
          {title}
          {badgeText && (
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${badgeClass}`}>
              {badgeText}
            </span>
          )}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && <div className="border-t px-4 py-3">{children}</div>}
    </div>
  );
}

function InfoRow({ icon, label, value }: {
  icon?: React.ReactNode; label: string; value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-xs break-words">{value}</div>
    </div>
  );
}

function CopyableRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        className={`group inline-flex w-full items-start gap-1.5 text-left text-xs hover:text-primary ${mono ? "font-mono" : ""}`}
        title="Copiar"
      >
        <span className="break-all min-w-0 flex-1">{value}</span>
        {copied ? (
          <Check size={11} className="text-emerald-500 shrink-0" />
        ) : (
          <Copy size={11} className="opacity-40 group-hover:opacity-100 shrink-0" />
        )}
      </button>
    </div>
  );
}

function StatusHistoryRow({ entry }: { entry: TokuStatusHistoryEntry }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <Clock size={10} className="text-muted-foreground shrink-0" />
      <span className="font-mono text-muted-foreground">
        {entry.timestamp ? formatDateTime(entry.timestamp) : "—"}
      </span>
      <span className="text-muted-foreground">
        {entry.old_status || "(inicio)"} → <span className="font-medium text-foreground">{entry.new_status}</span>
      </span>
    </div>
  );
}

// ─── Badges públicos (reusables en list) ─────────────────────────────────────
// rc58: nuevo estilo "Linear/Terminal" — dot circular + texto JetBrains Mono
// uppercase tracking-wider. Sin pill bg, sin border. Más limpio, más técnico.
export function EstadoBadge({ estado }: { estado: EstadoConciliacion }) {
  const map: Record<EstadoConciliacion, { label: string; dot: string; text: string }> = {
    conciliado:     { label: "Conciliado",     dot: "bg-success",  text: "text-success" },
    saldo_a_favor:  { label: "Saldo a favor",  dot: "bg-warn",     text: "text-warn" },
    anulado:        { label: "Anulado",        dot: "bg-danger",   text: "text-danger" },
  };
  const m = map[estado];
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider">
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      <span className={m.text}>{m.label}</span>
    </span>
  );
}

export function OrigenBadge({ origen }: { origen: OrigenPago }) {
  // rc58: outline icon del sprite + texto mono. Sin pill.
  const map: Record<OrigenPago, { label: string; iconName: import("@/components/icons/Icon").IconName }> = {
    toku_transfer:  { label: "Toku · SPEI",     iconName: "transfer" },
    toku_card:      { label: "Toku · Tarjeta",  iconName: "wallet" },
    toku_cash:      { label: "Toku · Efectivo", iconName: "wallet" },
    toku_wallet:    { label: "Toku · Wallet",   iconName: "wallet" },
    toku_other:     { label: "Toku",            iconName: "wallet" },
    manual:         { label: "Manual",          iconName: "user" },
    saldo_a_favor:  { label: "Saldo a favor",   iconName: "sparkles" },
  };
  const m = map[origen];
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      <Icon name={m.iconName} size={11} />
      {m.label}
    </span>
  );
}
